import { Box, Button, Divider, Flex, Group, IconFont, MultiSelect, Option, Select, Switch, Text, Toast, Tooltip } from '@byecode/ui'
import { DataSourceType, type FieldProtocol } from '@lighthouse/core'
import type { FlowNode, ImportExcelActionConfig, NodeTypes } from '@lighthouse/shared'
import {
    ActionDataNodeType,
    getInputInnerTypeMatch,
    getIsAppointField,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NO_EDIT_SYSTEM_FIELD_OMIT_ID,
    NodeFieldPreview,
    notImportableFieldTypes,
    OUTPUT_SINGLE_DATA_NODES,
    repeatModeOptions,
    SelectDataSource,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { workflowListAtom } from '@/atoms/workflow/state'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'
import { downloadImportTemplate } from '@/services'

import OutputVariable from '../../../OutputVariable'
import * as SC from '../../styles'

const SCxRepeatLabel = styled(Flex)`
    padding: 6px 16px;
    cursor: pointer;

    &:hover {
        background-color: var(--color-gray-100);
    }
`

interface ImportExcelActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: ImportExcelActionConfig
    onChange: (value: ImportExcelActionConfig) => void
}

export const ImportExcelActionConfigure: React.FC<ImportExcelActionConfigureProps> = ({ nodeType, allParentNodes, value, onChange }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const userOption = useGetUserRecordOptions()
    const [downloadLoading, setDownloadLoading] = useState(false)
    const options = useMemo(
        () => dataSourceList.filter(ds => ds.type === DataSourceType.dataSource || ds.type === DataSourceType.systemDataSource),
        [dataSourceList]
    )

    const singleDataNodes = useMemo(() => allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)), [allParentNodes])

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: singleDataNodes,
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, singleDataNodes, workflowList]
    )

    const selectedDataSource = useMemo(() => dataSourceList.find(ds => ds.id === value.dataSourceId), [dataSourceList, value.dataSourceId])

    const fieldMultiSelectOptions = useMemo(() => {
        const schema: Record<string, FieldProtocol> = selectedDataSource?.schema || {}
        return Object.values(schema)
            .filter(field => !notImportableFieldTypes.has(field.type) && !getIsAppointField(field.id, NO_EDIT_SYSTEM_FIELD_OMIT_ID))
            .map(field => ({
                label: field.name,
                value: field.id
            }))
    }, [selectedDataSource?.schema])

    const handleDownloadExcelTemplate = useCallback(async () => {
        if (!selectedDataSource || downloadLoading) {
            return
        }
        setDownloadLoading(true)
        await downloadImportTemplate({
            appId,
            envId,
            dsId: selectedDataSource.id,
            dsName: selectedDataSource.name
        })
        Toast.success('下载模版成功')
        setDownloadLoading(false)
    }, [appId, downloadLoading, envId, selectedDataSource])

    const importModeOptions: Option[] = useMemo(
        () => [...repeatModeOptions, { label: '导入报错', value: 'error', describe: '整个节点导入报错，不会有任何一条数据导入到数据表' }],
        []
    )

    const repeat = value.repeat

    return (
        <SC.Container>
            <Group label="目标导入数据表">
                <Flex gap={12} direction="column">
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>选择数据表</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <SelectDataSource
                                value={value.dataSourceId}
                                withinPortal
                                searchable
                                placeholder="请选择数据表"
                                onChange={dataSourceId => {
                                    if (dataSourceId === value.dataSourceId) {
                                        return
                                    }
                                    onChange({ ...value, dataSourceId, compareFields: [], requiredFields: [] })
                                }}
                                dataSourceList={options}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    {value.dataSourceId && (
                        <>
                            <Button style={{ width: '100%' }} loading={downloadLoading} onClick={handleDownloadExcelTemplate}>
                                下载模版
                            </Button>
                            <Box px={8}>
                                <Text size={12} color="var(--color-gray-400)" whiteSpace="pre-wrap" lineHeight="20px">
                                    导入模版由目标导入数据表生成的，需确保模版的表头名和对应数据表字段名称完全一致，否则会导入失败。
                                </Text>
                            </Box>
                        </>
                    )}
                </Flex>
            </Group>

            <Divider color="var(--color-gray-200)" />

            <Group
                label={
                    <PopoverQuestion
                        type="excelSource"
                        position="top"
                        renderTarget={(node, ref) => (
                            <Flex gap={16} ref={ref} alignItems="center">
                                <SC.FieldTitle required>导入的 Excel</SC.FieldTitle>
                                {node}
                            </Flex>
                        )}
                    />
                }
            >
                <SC.FieldList>
                    <SC.FieldItem>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            value={value.excelSource}
                            onBlur={excelSource => onChange({ ...value, excelSource })}
                            autoHeight
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                            placeholder="请输入"
                        />
                    </SC.FieldItem>
                </SC.FieldList>
            </Group>

            <Divider color="var(--color-gray-200)" />

            <Group label="导入设置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>必填字段</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <MultiSelect
                            searchable
                            placeholder="请选择"
                            options={fieldMultiSelectOptions}
                            value={value.requiredFields}
                            onChange={requiredFields => onChange({ ...value, requiredFields })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>

                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>识别重复项</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Switch
                            checked={repeat}
                            onChange={ev => {
                                const nextRepeat =
                                    typeof ev === 'boolean'
                                        ? ev
                                        : Boolean(
                                              (ev as unknown as { target?: { checked?: boolean }; currentTarget?: { checked?: boolean } })
                                                  ?.target?.checked ??
                                                  (
                                                      ev as unknown as {
                                                          target?: { checked?: boolean }
                                                          currentTarget?: { checked?: boolean }
                                                      }
                                                  )?.currentTarget?.checked
                                          )
                                onChange({ ...value, repeat: nextRepeat })
                            }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>

                {repeat && (
                    <>
                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel required>识别重复依据</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <MultiSelect
                                    searchable
                                    placeholder="请选择"
                                    options={fieldMultiSelectOptions}
                                    value={value.compareFields}
                                    onChange={compareFields => onChange({ ...value, compareFields })}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>

                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel required>重复记录处理</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <Select
                                    placeholder="请选择"
                                    options={importModeOptions}
                                    optionComponent={({ value, label, describe, ...rest }) => (
                                        <SCxRepeatLabel alignItems="center" gap={4} {...rest}>
                                            {label}
                                            <Tooltip
                                                withArrow
                                                styles={{
                                                    tooltip: {
                                                        maxWidth: '200px'
                                                    }
                                                }}
                                                title={describe}
                                            >
                                                <IconFont type="Question" size={16} color="var(--color-gray-400)" />
                                            </Tooltip>
                                        </SCxRepeatLabel>
                                    )}
                                    value={value.importMode}
                                    onChange={importMode => {
                                        if (!importMode) {
                                            return
                                        }
                                        onChange({ ...value, importMode: importMode as ImportExcelActionConfig['importMode'] })
                                    }}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>
                    </>
                )}
            </Group>

            <OutputVariable nodeType={ActionDataNodeType.IMPORT_EXCEL} />
        </SC.Container>
    )
}
